<template>
  <input
    ref="inputRef"
    :type="type"
    :class="[$style.input, { [$style['input--invalid']]: invalid }]"
    :disabled="disabledValue"
    :tabindex="tabIndex"
    :placeholder="placeholder"
    v-model="value"
    @focus="emit('focus')"
    @blur="emit('blur')"
    @paste="emit('paste', $event)"
    @keypress="emit('keypress', $event)"
    @keydown="emit('keydown', $event)"
  />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    type?: string
    disabled?: boolean
    modelValue?: string | number
    invalid?: boolean
    tabIndex?: string
    placeholder?: string
  }>(),
  {
    type: 'text',
    disabled: false,
    modelValue: '',
    invalid: false,
  }
)

const inputRef = ref<HTMLInputElement | null>(null)
defineExpose({ inputRef })

const emit = defineEmits<{
  (event: 'update:modelValue', value: string | number): void
  (event: 'focus'): void
  (event: 'blur'): void
  (event: 'paste', payload: ClipboardEvent): void
  (event: 'keypress', value: KeyboardEvent): void
  (event: 'keydown', value: KeyboardEvent): void
}>()

const value = computed({
  get(): string | number {
    return props.modelValue
  },
  set(value: string | number): void {
    emit('update:modelValue', value)
  },
})
const disabledValue = computed(
  (): true | undefined => props.disabled || undefined
)
</script>

<style lang="scss" module>
.input {
  box-sizing: border-box;
  padding: 10px 16px 11px;
  border: 1px solid $color-border-low-emphasis;
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-base;
  box-shadow: 0 0 0 56px $color-surface-primary inset; // autofill hack
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  line-height: 1.75;
  outline: 0;
  transition:
    border-color $transition-base,
    color $transition-base,
    -webkit-text-fill-color $transition-base;

  &::placeholder {
    color: $color-text-and-object-low-emphasis;
  }

  &:focus {
    border-color: $color-border-medium-emphasis;
  }

  &:disabled {
    border-color: $color-border-low-emphasis;
    box-shadow: 0 0 0 56px $color-surface-secondary inset; // autofill hack
    color: $color-text-and-object-low-emphasis;

    &:-webkit-autofill {
      border-color: $color-border-low-emphasis;
      box-shadow: 0 0 0 56px $color-surface-secondary inset; // autofill hack
      -webkit-text-fill-color: $color-text-and-object-low-emphasis;
    }
  }

  &--invalid {
    border-color: $color-caution-border;
    color: $color-caution-accent;
    box-shadow: 0 0 0 56px $color-caution-surface inset; // autofill hack

    &:-webkit-autofill {
      color: $color-caution-accent;
      box-shadow: 0 0 0 56px $color-caution-surface inset; // autofill hack
      -webkit-text-fill-color: $color-caution-accent;
    }

    &::placeholder {
      color: $color-caution-accent;
    }
  }
}
</style>
